import { useCallback, useEffect, useRef, useState } from "react";
import {
  initialState, step, forecast, forecastDeltas,
  ingressEgressAssessment, ritualContext, HORIZONS, TICK_MINUTES,
} from "./liveModel";

/* ──────────────────────────────────────────────────────────────────────────
 * Runs the live model and keeps a short history.
 *
 * The history is what makes §6 playback and §22 prediction accuracy real: a
 * forecast recorded at T can be compared against the actual at T+30 because
 * both are retained. Without it, "prediction accuracy" is a claim rather
 * than a measurement.
 *
 * Speed is in simulated minutes per real second. At 10x a viewer watches an
 * hour of operations in six minutes, which is the difference between a demo
 * that shows dynamics and one where a reviewer stares at a static screen and
 * takes your word for it.
 * ────────────────────────────────────────────────────────────────────────── */

const HISTORY_MINUTES = 180;

export function useLiveModel({ running = true, speed = 10 } = {}) {
  const [state, setState] = useState(() => initialState());
  const [history, setHistory] = useState([]);
  /* Forecasts are stamped with the minute they were MADE, so they can be
   * scored later against the actual for that target minute. */
  const [predictions, setPredictions] = useState([]);
  const raf = useRef(null);
  const last = useRef(0);

  useEffect(() => {
    if (!running) return undefined;
    const msPerTick = 1000 / Math.max(0.5, speed);

    const loop = (ts) => {
      if (!last.current) last.current = ts;
      if (ts - last.current >= msPerTick) {
        last.current = ts;
        setState((prev) => {
          const next = step(prev);

          setHistory((h) => {
            const appended = [...h, {
              minuteOfDay: next.minuteOfDay,
              sectors: next.sectors.map((s) => ({
                id: s.id, current: s.current, netPerMin: s.netPerMin,
                ingressEgressRatio: s.ingressEgressRatio,
              })),
            }];
            return appended.slice(-HISTORY_MINUTES);
          });

          /* Record a forecast every 15 simulated minutes. Sparse on purpose:
             a prediction every minute would bury the ones worth scoring. */
          if (next.minuteOfDay % 15 === 0) {
            const fc = forecast(next, HORIZONS);
            setPredictions((p) => [...p.slice(-40), {
              madeAt: next.minuteOfDay,
              horizons: Object.fromEntries(
                HORIZONS.map((h) => [h, fc[h].map((s) => ({ id: s.id, current: s.current }))])
              ),
            }]);
          }
          return next;
        });
      }
      raf.current = requestAnimationFrame(loop);
    };

    raf.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf.current); last.current = 0; };
  }, [running, speed]);

  /* §22 — score past forecasts against what actually happened. Only
   * predictions whose target minute has arrived are scored; the rest stay
   * pending rather than being counted as accurate by default. */
  const accuracy = useCallback(() => {
    const actualAt = (minute) => history.find((h) => h.minuteOfDay === minute);
    const scored = [];

    predictions.forEach((p) => {
      HORIZONS.forEach((h) => {
        const target = p.madeAt + h;
        const actual = actualAt(target);
        if (!actual) return;
        p.horizons[h].forEach((pred) => {
          const act = actual.sectors.find((s) => s.id === pred.id);
          if (!act || act.current === 0) return;
          scored.push({
            horizon: h,
            absPctError: Math.abs(pred.current - act.current) / act.current * 100,
          });
        });
      });
    });

    if (!scored.length) {
      return {
        available: false,
        reason: "No forecast has reached its target time yet. Accuracy is not yet measurable.",
      };
    }

    const byHorizon = HORIZONS.map((h) => {
      const rows = scored.filter((s) => s.horizon === h);
      return {
        horizon: h,
        samples: rows.length,
        mape: rows.length
          ? Number((rows.reduce((a, r) => a + r.absPctError, 0) / rows.length).toFixed(1))
          : null,
      };
    }).filter((r) => r.samples > 0);

    return { available: true, byHorizon, totalSamples: scored.length };
  }, [predictions, history]);

  const reset = useCallback(() => {
    setState(initialState());
    setHistory([]);
    setPredictions([]);
  }, []);

  return {
    state,
    history,
    tickMinutes: TICK_MINUTES,
    clock: state.minuteOfDay,
    forecast: (horizons = HORIZONS) => forecast(state, horizons),
    deltas: (h = 30) => forecastDeltas(state, h),
    ingressEgress: () => ingressEgressAssessment(state),
    context: ritualContext(state.minuteOfDay),
    accuracy,
    reset,
  };
}
